import React, { useCallback, useEffect } from "react";
import { createStackNavigator, StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "./RootNavigation";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import CheckClientInfo from "../screens/auth/CheckClientInfo";
import CheckPrivateClientInfo from "../screens/auth/CheckPrivateClientInfo";
import PhoneNumberScreen from "../screens/auth/PhoneNumber";
import ConfirmationScreen from "../screens/auth/ConfirmationCode";
import SelectRoleScreen from "../screens/auth/SelectRole";
import CheckWorkerInfo from "../screens/auth/CheckWorkerInfo";
import { useStepsHeader } from "../hooks/use-steps-header";
import { useTracking } from "../hooks/tracking";
import auth from "../contexts/FirebaseAuth";
import { Platform } from "react-native";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";

export const signupSigninScreens = [
    { screen: "PhoneNumber", index: 0 },
    { screen: "ConfirmCode", index: 1 },
    { screen: "SelectRole", index: 2 },
    { screen: "CheckWorkerInfo", index: 3 },
    { screen: "CheckClientInfo", index: 3 },
    { screen: "CheckPrivateClientInfo", index: 3 },
] as const;

export type SignupSigninStackParamList = {
    PhoneNumber: { isSignup?: boolean };
    ConfirmCode: { phoneNumber: string; isSignup: boolean };
    SelectRole?: { isSignup: boolean };
    CheckWorkerInfo: undefined;
    CheckClientInfo?: { isNewProfile: boolean };
    CheckPrivateClientInfo?: { isNewProfile: boolean };
};

export type SignupSigninScreens = keyof SignupSigninStackParamList;

const Stack = createStackNavigator<SignupSigninStackParamList>();

const StackStepInfo = {
    PhoneNumber: PhoneNumberScreen,
    ConfirmCode: ConfirmationScreen,
    SelectRole: SelectRoleScreen,
    CheckWorkerInfo: CheckWorkerInfo,
    CheckClientInfo: CheckClientInfo,
    CheckPrivateClientInfo: CheckPrivateClientInfo,
};

type SignupSigninStackProps = StackScreenProps<RootStackParamList, "SignupSigninStack">;

const SignupSigninStack: React.FC<SignupSigninStackProps> = ({ route, navigation }) => {
    const cardStyleInterpolator = useCardStyleInterpolator();
    const { isSignup = false, initialRouteName = "PhoneNumber" } = route.params || {};
    const { trackEvent } = useTracking();
    const isLarge = useIsScreenSizeMin("large");
    const getCurrentIndex = useCallback(
        (routeName: keyof SignupSigninStackParamList) => {
            const indexOffset = isLarge ? 2 : 0; // on web we do not have first 2 steps
            const screenIndex = signupSigninScreens.find((r) => r.screen === routeName)?.index;
            if (screenIndex === undefined) {
                return 0; // fallback to 0 if the screen is not found
            }
            return screenIndex - indexOffset;
        },
        [isLarge],
    );

    const getTotalSteps = useCallback(
        (routeName: keyof SignupSigninStackParamList) => {
            // on web we do not have first 2 steps so we always use 2 steps
            if (!isLarge && (isSignup || getCurrentIndex(routeName) > 1)) {
                return 4;
            } else {
                return 2;
            }
        },
        [getCurrentIndex, isSignup, isLarge],
    );

    const { screenListeners, headerComponent } = useStepsHeader<keyof SignupSigninStackParamList>({
        getCurrentIndex,
        getTotalSteps,
        onClose: () => {
            // we signout the firebase user to allow a new connection with other phone number
            if (auth().currentUser) {
                void auth().signOut();
            }
            if (Platform.OS === "web") {
                // on web we need to reload for the firebase recaptcha to be reset
                window.location.reload();
            } else {
                navigation.reset({
                    index: 0,
                    routes: [{ name: "Welcome" }],
                });
            }
        },
    });

    useEffect(() => {
        if (isSignup) {
            void trackEvent("signup_showed");
        }
    }, [isSignup, trackEvent]);

    return (
        <>
            {headerComponent}
            <Stack.Navigator
                initialRouteName={initialRouteName}
                screenListeners={screenListeners}
                screenOptions={{
                    headerMode: "float",
                    headerTransparent: false,
                    headerShadowVisible: false,
                    headerBackButtonDisplayMode: "minimal",
                    headerTitleAlign: "center",
                    headerShown: false,
                    cardStyleInterpolator,
                }}
            >
                {signupSigninScreens.map(({ screen }) => (
                    <Stack.Screen
                        key={screen}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        name={screen}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        initialParams={{ isSignup }}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StackStepInfo[screen]}
                    />
                ))}
            </Stack.Navigator>
        </>
    );
};

export default SignupSigninStack;
